/* Flyer Editor — bring ANY flyer/post image (AI-generated, Nano Banana, uploaded)
   into a layered canvas and add editable elements on top: text (with brand fonts,
   sizes, colors), logo, headshot, branded QR code and contact info (phone / email /
   website). Drag to position, click to edit. Exports a crisp PNG via html-to-image
   and saves to My Flyers or back onto the originating Content-Plan post. */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as htmlToImage from 'html-to-image';
import { makeQrDataUrl } from '../../lib/qr';
import {
  Type, Image as ImageIcon, QrCode, Phone, Mail, Globe, User, Trash2, Save, Download,
  Upload, Loader2, ArrowUp, ArrowDown, Bold, Send, Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });
const errMsg = (e, f) => { const d = e?.response?.data?.detail; return !d ? f : (typeof d === 'string' ? d : d.message || f); };

export const EDITOR_FONTS = [
  { id: 'montserrat', name: 'Montserrat', css: "'Montserrat', sans-serif" },
  { id: 'poppins', name: 'Poppins', css: "'Poppins', sans-serif" },
  { id: 'anton', name: 'Anton (impact)', css: "'Anton', sans-serif" },
  { id: 'bebas', name: 'Bebas Neue', css: "'Bebas Neue', sans-serif" },
  { id: 'oswald', name: 'Oswald', css: "'Oswald', sans-serif" },
  { id: 'playfair', name: 'Playfair Display', css: "'Playfair Display', serif" },
  { id: 'archivo', name: 'Archivo Black', css: "'Archivo Black', sans-serif" },
  { id: 'roboto', name: 'Roboto', css: "'Roboto', sans-serif" },
];

const clamp = (v) => Math.max(-4, Math.min(98, v));
const uid = () => 'el_' + Math.random().toString(36).slice(2, 9);
const readFile = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

export default function FlyerEditor({ brand, brands = [], initialImageUrl = '', initialTitle = '', planId = null, itemId = null, onSaved }) {
  const [bg, setBg] = useState(initialImageUrl || '');
  const [dims, setDims] = useState({ w: 1080, h: 1350 });
  const [els, setEls] = useState([]);
  const [sel, setSel] = useState(null);
  const [title, setTitle] = useState(initialTitle || 'Edited flyer');
  const [brandId, setBrandId] = useState(brand?.brand_id || brands[0]?.brand_id || '');
  const [busy, setBusy] = useState('');

  const stageRef = useRef(null);
  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const bgInput = useRef(null);

  const activeBrand = brands.find((b) => b.brand_id === brandId) || brand || brands[0];

  useEffect(() => { setBg(initialImageUrl || ''); setTitle(initialTitle || 'Edited flyer'); setEls([]); setSel(null); }, [initialImageUrl, initialTitle]);

  // detect natural size of the imported image so the canvas matches it (square posts, 4:5 flyers, etc.)
  useEffect(() => {
    if (!bg) { setDims({ w: 1080, h: 1350 }); return; }
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { if (img.naturalWidth) setDims({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.onerror = () => setDims({ w: 1080, h: 1350 });
    img.src = bg;
  }, [bg]);

  // drag + resize handling on the whole window (works against the displayed stage size)
  useEffect(() => {
    const move = (e) => {
      const rz = resizeRef.current;
      if (rz) {
        const dxPct = (e.clientX - rz.startX) / rz.rw * 100;
        setEls((s) => s.map((x) => {
          if (x.id !== rz.id) return x;
          const newW = Math.max(6, Math.min(100, rz.ow + dxPct));
          if (x.type === 'text') {
            const ratio = rz.ow ? newW / rz.ow : 1;
            return { ...x, wPct: newW, fontSize: Math.max(10, Math.round(rz.ofs * ratio)) };
          }
          return { ...x, wPct: newW };
        }));
        return;
      }
      const d = dragRef.current; if (!d) return;
      const dx = (e.clientX - d.startX) / d.rw * 100;
      const dy = (e.clientY - d.startY) / d.rh * 100;
      setEls((s) => s.map((x) => (x.id === d.id ? { ...x, xPct: clamp(d.ox + dx), yPct: clamp(d.oy + dy) } : x)));
    };
    const up = () => { dragRef.current = null; resizeRef.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, []);

  const startDrag = (id) => (e) => {
    e.preventDefault(); e.stopPropagation();
    setSel(id);
    const rect = wrapRef.current.getBoundingClientRect();
    const el = els.find((x) => x.id === id);
    if (!el) return;
    dragRef.current = { id, startX: e.clientX, startY: e.clientY, ox: el.xPct, oy: el.yPct, rw: rect.width, rh: rect.height };
  };

  const startResize = (id) => (e) => {
    e.preventDefault(); e.stopPropagation();
    setSel(id);
    const rect = wrapRef.current.getBoundingClientRect();
    const el = els.find((x) => x.id === id);
    if (!el) return;
    resizeRef.current = { id, startX: e.clientX, rw: rect.width, ow: el.wPct, ofs: el.fontSize || 0 };
  };

  // New elements cascade so they never stack on the exact same spot (fixes collision).
  const addEl = (el) => {
    const id = uid();
    setEls((s) => {
      const n = s.length;
      const xPct = 8 + (n % 5) * 9;
      const yPct = 8 + (n % 6) * 12;
      return [...s, { id, xPct, yPct, ...el }];
    });
    setSel(id);
  };
  const baseFont = Math.max(28, Math.round(dims.w * 0.05));
  const c = activeBrand?.colors || {};

  const addText = (text = 'Your text here', extra = {}) => addEl({
    type: 'text', text, fontSize: baseFont, color: '#FFFFFF', fontFamily: EDITOR_FONTS[0].css,
    fontWeight: 800, align: 'left', wPct: 60, bgColor: '', padX: 18, padY: 12, radius: 12, ...extra,
  });
  const addImage = (src, wPct = 26, isQr = false) => { if (!src) { toast.error('Nothing to add for this brand'); return; } addEl({ type: 'image', src, wPct, isQr }); };
  const addQr = async () => {
    const url = window.prompt('QR code link (e.g. https://calendly.com/you or your website):', activeBrand?.website ? `https://${activeBrand.website.replace(/^https?:\/\//, '')}` : 'https://');
    if (!url) return;
    const dataUrl = await makeQrDataUrl(url, c.primary || '#0A0F1C');
    if (!dataUrl) { toast.error('Could not generate QR'); return; }
    addImage(dataUrl, 16, true);
  };

  // One-click brand preset: drops logo (top-left), a contact bar (bottom), and a
  // website QR (bottom-right) onto the imported design in the brand's colors.
  const brandImage = async () => {
    const adds = [];
    if (activeBrand?.logo_url) adds.push({ type: 'image', src: activeBrand.logo_url, wPct: 24, xPct: 4, yPct: 4 });
    const parts = [activeBrand?.phone, activeBrand?.website, activeBrand?.email].filter(Boolean);
    if (parts.length) {
      adds.push({
        type: 'text', text: parts.join('   •   '), fontSize: Math.max(22, Math.round(dims.w * 0.032)),
        color: '#FFFFFF', fontFamily: EDITOR_FONTS[0].css, fontWeight: 800, align: 'center',
        wPct: 88, xPct: 6, yPct: 90, bgColor: c.primary || '#1E5BA8', padX: 24, padY: 14, radius: 14,
      });
    }
    if (activeBrand?.website) {
      const url = 'https://' + activeBrand.website.replace(/^https?:\/\//, '');
      const qr = await makeQrDataUrl(url, c.primary || '#0A0F1C');
      if (qr) adds.push({ type: 'image', src: qr, wPct: 15, xPct: 80, yPct: 70, isQr: true });
    }
    if (!adds.length) { toast.error('Add brand details (logo, phone, website) in Brand Kit first'); return; }
    setEls((s) => [...s, ...adds.map((a) => ({ id: uid(), ...a }))]);
    toast.success('Brand elements added — drag to fine-tune, then Save');
  };

  const selEl = els.find((x) => x.id === sel);
  const updateSel = (patch) => setEls((s) => s.map((x) => (x.id === sel ? { ...x, ...patch } : x)));
  const delSel = () => { setEls((s) => s.filter((x) => x.id !== sel)); setSel(null); };
  const moveLayer = (dir) => setEls((s) => {
    const i = s.findIndex((x) => x.id === sel); if (i < 0) return s;
    const j = dir === 'up' ? i + 1 : i - 1; if (j < 0 || j >= s.length) return s;
    const cp = [...s]; const [it] = cp.splice(i, 1); cp.splice(j, 0, it); return cp;
  });

  const exportPng = useCallback(async () => {
    setSel(null);
    // tiny delay so any just-added DOM (fonts/images) + deselect settle
    await new Promise((r) => setTimeout(r, 160));
    return htmlToImage.toPng(stageRef.current, { width: dims.w, height: dims.h, pixelRatio: 1, cacheBust: true });
  }, [dims]);

  const save = async () => {
    if (!brandId) { toast.error('Pick a brand to save under'); return; }
    setBusy('save');
    try {
      const dataUrl = await exportPng();
      await api.post('/api/flyer-studio/flyers', { brand_id: brandId, title: title || 'Edited flyer', data_url: dataUrl });
      toast.success('Saved to My Flyers');
      onSaved?.();
    } catch (e) { toast.error(errMsg(e, 'Could not save flyer')); } finally { setBusy(''); }
  };
  const download = async () => {
    setBusy('dl');
    try { const u = await exportPng(); const a = document.createElement('a'); a.href = u; a.download = `${(title || 'flyer').replace(/\s+/g, '-')}.png`; a.click(); }
    catch (e) { toast.error('Could not export image'); } finally { setBusy(''); }
  };
  const attachBack = async () => {
    if (!planId || !itemId) return;
    setBusy('attach');
    try {
      const dataUrl = await exportPng();
      await api.post('/api/flyer-studio/attach-to-plan-item', { plan_id: planId, item_id: itemId, data_url: dataUrl });
      toast.success('Saved back onto the original post');
      onSaved?.();
    } catch (e) { toast.error(errMsg(e, 'Could not save back to the post')); } finally { setBusy(''); }
  };

  const onUploadBg = async (file) => { try { setBg(await readFile(file)); setEls([]); setSel(null); } catch { toast.error('Could not load image'); } };

  // display scaling
  const maxW = 440, maxH = 600;
  const scale = Math.min(maxW / dims.w, maxH / dims.h, 1);
  const dispW = Math.round(dims.w * scale);
  const dispH = Math.round(dims.h * scale);

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-5" data-testid="flyer-editor">
      {/* CANVAS */}
      <div>
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4 mb-3">
          <div className="flex items-center gap-2 flex-wrap" data-testid="flyer-editor-toolbar">
            <button onClick={brandImage} data-testid="fe-brand-image" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1E5BA8] text-white text-xs font-bold hover:bg-[#1A4F94]">
              <Sparkles size={14} /> Brand this image
            </button>
            <span className="w-px h-6 bg-[#E2E8F0] mx-0.5" />
            <ToolBtn icon={Type} label="Text" onClick={() => addText()} tid="fe-add-text" />
            <ToolBtn icon={ImageIcon} label="Logo" onClick={() => addImage(activeBrand?.logo_url, 30)} tid="fe-add-logo" />
            <ToolBtn icon={User} label="Headshot" onClick={() => addImage(activeBrand?.headshot_url, 28)} tid="fe-add-headshot" />
            <ToolBtn icon={QrCode} label="QR code" onClick={addQr} tid="fe-add-qr" />
            <ToolBtn icon={Phone} label="Phone" onClick={() => addText(activeBrand?.phone || 'Call me', { fontWeight: 700, color: c.secondary || '#E27A22' })} tid="fe-add-phone" />
            <ToolBtn icon={Mail} label="Email" onClick={() => addText(activeBrand?.email || 'you@email.com', { fontWeight: 700 })} tid="fe-add-email" />
            <ToolBtn icon={Globe} label="Website" onClick={() => addText(activeBrand?.website || 'yourwebsite.com', { fontWeight: 700 })} tid="fe-add-website" />
            <input ref={bgInput} type="file" accept="image/*" className="hidden" data-testid="fe-bg-input" onChange={(e) => e.target.files[0] && onUploadBg(e.target.files[0])} />
            <ToolBtn icon={Upload} label="Background" onClick={() => bgInput.current?.click()} tid="fe-upload-bg" />
          </div>
        </div>

        <div className="flex justify-center bg-[#F1F5F9] rounded-2xl border border-[#E2E8F0] p-5">
          <div ref={wrapRef} style={{ width: dispW, height: dispH, position: 'relative', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', borderRadius: 6, overflow: 'hidden' }} onMouseDown={() => setSel(null)}>
            <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
              <div ref={stageRef} style={{ width: dims.w, height: dims.h, position: 'relative', background: bg ? 'transparent' : (c.primary || '#0A3D7A') }} data-testid="flyer-editor-stage">
                {bg && <img src={bg} crossOrigin="anonymous" alt="" style={{ position: 'absolute', inset: 0, width: dims.w, height: dims.h, objectFit: 'cover', display: 'block' }} />}
                {els.map((el) => {
                  const selected = sel === el.id;
                  const handle = selected ? (
                    <div onMouseDown={startResize(el.id)} data-testid={`fe-resize-${el.id}`}
                      style={{ position: 'absolute', right: -9, bottom: -9, width: 18, height: 18, borderRadius: '50%', background: '#1E5BA8', border: '2px solid #fff', cursor: 'nwse-resize', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
                  ) : null;
                  const wrap = { position: 'absolute', left: `${el.xPct}%`, top: `${el.yPct}%`, width: `${el.wPct}%`, cursor: 'move', outline: selected ? '3px dashed #1E5BA8' : 'none', outlineOffset: 4, boxSizing: 'border-box' };
                  if (el.type === 'text') {
                    return (
                      <div key={el.id} onMouseDown={startDrag(el.id)} style={wrap} data-testid={`fe-el-${el.id}`}>
                        <div style={{
                          fontFamily: el.fontFamily, fontSize: el.fontSize, color: el.color,
                          fontWeight: el.fontWeight, textAlign: el.align, lineHeight: 1.12, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          background: el.bgColor || 'transparent', padding: el.bgColor ? `${el.padY}px ${el.padX}px` : 0, borderRadius: el.bgColor ? el.radius : 0,
                          textShadow: el.bgColor ? 'none' : '0 2px 12px rgba(0,0,0,0.35)',
                        }}>{el.text}</div>
                        {handle}
                      </div>
                    );
                  }
                  return (
                    <div key={el.id} onMouseDown={startDrag(el.id)} style={wrap} data-testid={`fe-el-${el.id}`}>
                      <img src={el.src} crossOrigin="anonymous" alt="" draggable={false} style={{
                        width: '100%', height: 'auto', display: 'block',
                        background: el.isQr ? '#fff' : 'transparent', padding: el.isQr ? '1.4%' : 0, borderRadius: el.isQr ? 10 : 0,
                      }} />
                      {handle}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <p className="text-xs text-[#94A3B8] mt-2 text-center">Click an element to select · drag to move · drag the blue corner handle to resize · {dims.w}×{dims.h}px</p>
      </div>

      {/* PROPERTIES + ACTIONS */}
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4 space-y-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Save under brand</label>
            <select value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="fe-brand-select" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
              {brands.map((b) => <option key={b.brand_id} value={b.brand_id}>{b.name}{b.is_default ? ' ★' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="fe-title" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
          </div>
          <div className="flex flex-col gap-2 pt-1">
            <button onClick={save} disabled={!!busy} data-testid="fe-save" className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold hover:bg-[#1A4F94] disabled:opacity-60">
              {busy === 'save' ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save to My Flyers
            </button>
            {planId && itemId && (
              <button onClick={attachBack} disabled={!!busy} data-testid="fe-attach" className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0F766E] text-white text-sm font-bold hover:bg-[#0d655e] disabled:opacity-60">
                {busy === 'attach' ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Save back onto the post
              </button>
            )}
            <button onClick={download} disabled={!!busy} data-testid="fe-download" className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-sm font-bold hover:border-[#1E5BA8] disabled:opacity-60">
              {busy === 'dl' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download PNG
            </button>
          </div>
        </div>

        {selEl ? (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4 space-y-3" data-testid="fe-props">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-[#64748B]">{selEl.type === 'text' ? 'Edit text' : (selEl.isQr ? 'QR code' : 'Image')}</span>
              <div className="flex gap-1.5">
                <button onClick={() => moveLayer('up')} title="Bring forward" className="text-[#64748B] hover:text-[#1E5BA8]" data-testid="fe-layer-up"><ArrowUp size={15} /></button>
                <button onClick={() => moveLayer('down')} title="Send backward" className="text-[#64748B] hover:text-[#1E5BA8]" data-testid="fe-layer-down"><ArrowDown size={15} /></button>
                <button onClick={delSel} title="Delete" className="text-[#94A3B8] hover:text-[#DC2626]" data-testid="fe-del"><Trash2 size={15} /></button>
              </div>
            </div>

            {selEl.type === 'text' ? (
              <>
                <textarea value={selEl.text} onChange={(e) => updateSel({ text: e.target.value })} rows={2} data-testid="fe-text-input" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">Font</label>
                  <select value={selEl.fontFamily} onChange={(e) => updateSel({ fontFamily: e.target.value })} data-testid="fe-font" className="w-full mt-1 px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-sm">
                    {EDITOR_FONTS.map((f) => <option key={f.id} value={f.css}>{f.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">Size · {selEl.fontSize}px</label>
                  <input type="range" min="14" max={Math.round(dims.w * 0.18)} value={selEl.fontSize} onChange={(e) => updateSel({ fontSize: +e.target.value })} data-testid="fe-size" className="w-full" />
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-[#475569]">Color
                    <input type="color" value={selEl.color} onChange={(e) => updateSel({ color: e.target.value })} data-testid="fe-color" className="w-8 h-8 rounded border border-[#E2E8F0]" /></label>
                  <button onClick={() => updateSel({ fontWeight: selEl.fontWeight >= 700 ? 500 : 900 })} data-testid="fe-bold" className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border text-xs font-bold ${selEl.fontWeight >= 700 ? 'bg-[#1E5BA8] text-white border-[#1E5BA8]' : 'border-[#E2E8F0] text-[#475569]'}`}><Bold size={13} /> Bold</button>
                  <select value={selEl.align} onChange={(e) => updateSel({ align: e.target.value })} data-testid="fe-align" className="px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs font-semibold">
                    <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                  </select>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-[#475569]">Highlight
                    <input type="color" value={selEl.bgColor || '#1E5BA8'} onChange={(e) => updateSel({ bgColor: e.target.value })} data-testid="fe-bgcolor" className="w-8 h-8 rounded border border-[#E2E8F0]" /></label>
                  {selEl.bgColor && <button onClick={() => updateSel({ bgColor: '' })} className="text-xs font-bold text-[#94A3B8] hover:text-[#DC2626]" data-testid="fe-bgclear">Remove highlight</button>}
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">Box width · {selEl.wPct}%</label>
                  <input type="range" min="15" max="95" value={selEl.wPct} onChange={(e) => updateSel({ wPct: +e.target.value })} className="w-full" />
                </div>
              </>
            ) : (
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">Size · {selEl.wPct}%</label>
                <input type="range" min="6" max="80" value={selEl.wPct} onChange={(e) => updateSel({ wPct: +e.target.value })} data-testid="fe-img-size" className="w-full" />
              </div>
            )}
          </div>
        ) : (
          <div className="bg-[#F8FAFC] rounded-2xl border border-dashed border-[#E2E8F0] p-4 text-xs text-[#64748B]" data-testid="fe-hint">
            Add elements from the toolbar, then click any element on the flyer to edit its text, font, size, color or position. Everything is layered on top of your imported design — nothing about the original image is lost.
          </div>
        )}
      </div>
    </div>
  );
}

function ToolBtn({ icon: Icon, label, onClick, tid }) {
  return (
    <button onClick={onClick} data-testid={tid} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold hover:border-[#1E5BA8] hover:text-[#1E5BA8] transition-colors">
      <Icon size={14} /> {label}
    </button>
  );
}
